import { useTranslation } from "react-i18next";
import { Maximize2, Move } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import { AlreadyHereBubble } from "./components/already-here-bubble";
import { MayaSvgAvatar } from "./components/maya-svg-avatar";
import { OrbitParticles } from "./components/orbit-particles";
import { FAB_SIZE_PX, MAYA_SIDEBAR_ID, STORAGE_KEYS } from "./constants";
import { useMayaSidebar } from "./context/use-maya-sidebar";
import { useComposerClearance } from "./hooks/use-composer-clearance";
import { useCursorTracking } from "./hooks/use-cursor-tracking";
import { useDraggableFab } from "./hooks/use-draggable-fab";
import { useResizableFab } from "./hooks/use-resizable-fab";
import { pingComposer } from "./utils/composer-ping";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

import "./maya-copilot.css";

export { MayaPageSeam } from "./components/page-seam";
export { MayaSidebar } from "./components/maya-sidebar";
export { MayaSidebarProvider } from "./context/sidebar-provider";

/**
 * Maya's floating launcher. A tap opens (or closes) the chat sidebar beside
 * the page; while the sidebar is open the launcher steps aside — Maya lives
 * in the sidebar then — and comes back when it closes. ⌘K toggles too (see
 * MayaSidebarProvider).
 *
 * On the AI Assistant page, where Maya is the page, a tap just says so: a
 * short note, and the page's composer glows.
 */
export const MayaCopilot = () => {
  const { t } = useTranslation(["common"]);
  const {
    open,
    toggle,
    triggerRef,
    warp,
    returnFocusRef,
    onAssistantPage,
    assistantComposerRef,
  } = useMayaSidebar();
  const { pathname } = useLocation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The page the "already here" note was opened on, so it never outlives
  // that page (Back, a link, a new chat's url).
  const [bubbleOn, setBubbleOn] = useState<string | null>(null);
  const closeBubble = useCallback(() => setBubbleOn(null), []);
  // Hidden while Maya lives in the sidebar, and on the way back until the
  // returning stars land here (see MayaWarpOverlay).
  const hidden =
    open || (warp?.direction === "out" && warp.phase === "travelling");

  // A close that took focus out of the sidebar hands it back here — but only
  // once the launcher is visible again (it is `inert` while the stars fly).
  useEffect(() => {
    if (hidden || !returnFocusRef.current) return;
    returnFocusRef.current = false;
    triggerRef.current?.focus({ preventScroll: true });
  }, [hidden, returnFocusRef, triggerRef]);
  const headRef = useRef<SVGGElement | null>(null);
  const leftPupilRef = useRef<SVGGElement | null>(null);
  const rightPupilRef = useRef<SVGGElement | null>(null);

  const reducedMotion = usePrefersReducedMotion();

  const {
    size: fabSize,
    resizing,
    onResizePointerDown,
    consumeResizeClick,
  } = useResizableFab({ storageKey: STORAGE_KEYS.fabSize });
  const { position, dragging, onPointerDown, consumeDragClick } =
    useDraggableFab({ storageKey: STORAGE_KEYS.fabPosition, size: fabSize });
  // Scale the avatar artwork to match the chosen FAB diameter.
  const avatarScale = fabSize / FAB_SIZE_PX;
  // On the AI Assistant page it rises above the composer it would cover;
  // never mid-drag, where it must stay under the pointer.
  const lift = useComposerClearance({
    enabled: onAssistantPage && !hidden && !dragging,
    composerRef: assistantComposerRef,
    x: position.x,
    y: position.y,
    size: fabSize,
  });
  const top = position.y - lift;

  // The face tilts and the eyes glance toward the cursor wherever the FAB
  // sits. Disabled mid-drag (the FAB itself is moving) and while hidden.
  // Eyes also blink via CSS.
  useCursorTracking({
    anchorRef: triggerRef,
    headRef,
    leftPupilRef,
    rightPupilRef,
    enabled: !reducedMotion && !dragging && !hidden,
  });

  const handleFabClick = useCallback(() => {
    // Swallow the click that ends a drag or resize so Maya doesn't open.
    if (consumeDragClick() || consumeResizeClick()) return;
    if (onAssistantPage) {
      if (bubbleOn === pathname) {
        setBubbleOn(null);
        return;
      }
      setBubbleOn(pathname);
      const input = assistantComposerRef.current?.inputRef.current;
      if (input && !reducedMotion) pingComposer(input);
      return;
    }
    toggle();
  }, [
    consumeDragClick,
    consumeResizeClick,
    onAssistantPage,
    bubbleOn,
    pathname,
    assistantComposerRef,
    reducedMotion,
    toggle,
  ]);
  const bubbleShown = onAssistantPage && bubbleOn === pathname && !hidden;

  return createPortal(
    <div
      ref={containerRef}
      // Hidden, not unmounted, while the sidebar is open: a remount would
      // replay the drop-in animation every time the sidebar closes. It
      // implodes quickly into the warp's stars and re-forms with an overshoot.
      inert={hidden}
      // z-45: above page chrome (z-40), below modal overlays (z-50) so it stays behind their backdrop.
      className={`maya-fab group fixed z-45 ${
        hidden
          ? "pointer-events-none scale-50 opacity-0"
          : "scale-100 opacity-100"
      }`}
      style={{
        left: position.x,
        top,
        transition:
          dragging || reducedMotion
            ? "none"
            : hidden
              ? "left 0.25s ease, top 0.25s ease, opacity 0.15s ease, scale 0.15s ease-in"
              : "left 0.25s ease, top 0.25s ease, opacity 0.25s ease, scale 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)",
        touchAction: "none",
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        onPointerDown={onPointerDown}
        onClick={handleFabClick}
        // On the AI Assistant page it shows the "already here" note.
        aria-label={
          onAssistantPage
            ? t("common:maya.already_here_label")
            : t("common:a11y.open_maya_copilot")
        }
        aria-controls={onAssistantPage ? undefined : MAYA_SIDEBAR_ID}
        aria-expanded={onAssistantPage ? undefined : open}
        aria-keyshortcuts={onAssistantPage ? undefined : "Meta+K Control+K"}
        title={
          onAssistantPage
            ? t("common:maya.already_here_label")
            : t("common:a11y.maya_copilot_tooltip")
        }
        style={{ width: fabSize, height: fabSize }}
        className={`maya-drop-in maya-fab-press relative flex items-center justify-center rounded-full bg-surface shadow-xl border border-primary/15 select-none touch-none hover:shadow-2xl focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-primary ${
          dragging ? "cursor-grabbing" : "cursor-grab"
        }`}
      >
        {/* Drag affordance — appears top-right on hover. */}
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute -top-1 -right-1 z-10 grid size-5 place-items-center rounded-full bg-primary text-primary-foreground shadow-md transition-opacity ${
            dragging ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <Move className="size-3" />
        </span>
        <OrbitParticles enabled={!reducedMotion && !dragging && !hidden} />
        <span
          className="flex items-center justify-center"
          style={{ scale: String(avatarScale) }}
        >
          <MayaSvgAvatar
            headRef={headRef}
            leftEyeRef={leftPupilRef}
            rightEyeRef={rightPupilRef}
          />
        </span>
      </button>
      {/* Resize affordance — drag the bottom-left corner to scale the FAB.
          Physical left/right on purpose, as with the drag badge: the FAB is
          placed in physical coordinates and useResizableFab grows it as this
          handle moves left/down, in either text direction. */}
      <button
        type="button"
        onPointerDown={onResizePointerDown}
        aria-label={t("common:a11y.resize_maya_button")}
        title={t("common:a11y.drag_to_resize")}
        className={`absolute -bottom-1 -left-1 z-10 grid size-5 cursor-nesw-resize place-items-center rounded-full bg-primary text-primary-foreground shadow-md touch-none transition-opacity ${
          resizing
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
        }`}
      >
        <Maximize2 className="size-3" />
      </button>
      {/* Always in the page there, so the note is announced when it shows. */}
      {onAssistantPage && (
        <span role="status" className="sr-only">
          {bubbleShown ? t("common:maya.already_here") : ""}
        </span>
      )}
      {bubbleShown && (
        <AlreadyHereBubble
          above={top > 120}
          alignRight={position.x + fabSize / 2 > window.innerWidth / 2}
          containerRef={containerRef}
          onClose={closeBubble}
        />
      )}
    </div>,
    document.body,
  );
};

export default MayaCopilot;
